import { Check } from 'lucide-react';

import Docker from '@/assets/ico/vendor/docker.svg?c';
import Podman from '@/assets/ico/vendor/podman.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';

import { Badge } from '@@/Badge';
import { DetailsTable } from '@@/DetailsTable';
import { InlineLoader } from '@@/InlineLoader';
import { Alert } from '@@/Alert';

import { usePolicy } from '../queries/usePolicy';
import {
  getPolicyTypeDisplayName,
  getEnvironmentTypeDisplayName,
  PolicyEnvironmentType,
} from '../types';

interface Props {
  policyId: number;
}

export function PolicyDetailsTab({ policyId }: Props) {
  const policyQuery = usePolicy(policyId);
  const policy = policyQuery.data;

  const isActive =
    policy?.EnvironmentGroups && policy?.EnvironmentGroups?.length > 0;

  const EnvironmentIcon = getEnvironmentTypeIcon(policy?.EnvironmentType);

  if (policyQuery.isLoading) {
    return <InlineLoader>Loading...</InlineLoader>;
  }
  if (policyQuery.error) {
    return (
      <Alert color="error" title="Error loading policy">
        {(policyQuery.error as Error)?.message ?? 'Unknown error'}
      </Alert>
    );
  }
  if (!policy) {
    return (
      <Alert color="error" title="Policy not found">
        Policy not found
      </Alert>
    );
  }

  return (
    <DetailsTable dataCy="policy-details-table">
      {policy?.Name && (
        <tr>
          <td className="min-w-[150px]">Policy name</td>
          <td data-cy="detailsTable-Policy nameValue">
            <span>{policy.Name}</span>
          </td>
        </tr>
      )}

      {policy?.Type && (
        <tr>
          <td className="min-w-[150px]">Type</td>
          <td data-cy="detailsTable-TypeValue">
            <Badge type="info">{getPolicyTypeDisplayName(policy.Type)}</Badge>
          </td>
        </tr>
      )}

      {policy?.EnvironmentType && (
        <tr>
          <td className="min-w-[150px]">Environment type</td>
          <td data-cy="detailsTable-EnvironmentTypeValue">
            <div className="flex items-center gap-2">
              <Badge type="muted" className="min-w-max">
                <EnvironmentIcon className="h-4 w-4" />
                {getEnvironmentTypeDisplayName(policy.EnvironmentType)}
              </Badge>
            </div>
          </td>
        </tr>
      )}

      {policy?.UpdatedAt && (
        <tr>
          <td className="min-w-[150px]">Last updated</td>
          <td data-cy="detailsTable-Last updatedValue">
            <span className="text-muted">
              {policy.UpdatedAt
                ? new Date(policy.UpdatedAt).toLocaleString()
                : 'Unknown'}
            </span>
          </td>
        </tr>
      )}

      <tr>
        <td className="min-w-[150px]">Status</td>
        <td data-cy="detailsTable-StatusValue">
          {isActive && policy?.EnvironmentGroups ? (
            <div className="flex gap-2 flex-wrap items-center">
              {/* TODO: show the actual status */}
              <Badge type="muted">
                <Check className="h-3 w-3 mr-1" />
                {policy?.EnvironmentGroups.length === 1
                  ? `Applied to ${policy?.EnvironmentGroups.length} environment group`
                  : `Applied to ${policy?.EnvironmentGroups.length} environment groups`}
              </Badge>
            </div>
          ) : (
            <Badge type={isActive ? 'success' : 'warn'}>
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </td>
      </tr>
    </DetailsTable>
  );
}

// Get the appropriate icon based on environment type
function getEnvironmentTypeIcon(environmentType?: PolicyEnvironmentType) {
  switch (environmentType) {
    case 'kubernetes':
      return Kubernetes;
    case 'podman':
      return Podman;
    case 'docker':
    case 'swarm':
    default:
      return Docker;
  }
}
